// Configuración del Asistente del ERP (chat/voz). Sistemas → Asistente → Configuración.
// Gobierna al agente (scripts 311/314): qué ACCIÓN puede pedir cada PERFIL en la
// empresa actual (matriz acciones × perfiles) y qué DISPOSITIVOS (nº WhatsApp,
// web, app) están vinculados a qué usuario. Sin habilitación ni vínculo, el
// asistente conversa pero no ejecuta nada.
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Loader2, Mic, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { auth, Campo, fieldCls, Modal, RIESGO_BADGE, SELECT_PLACEHOLDER } from './shared';
import type { RiesgoAsistente } from './shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';

const ASIST = { headers: { 'Content-Profile': 'asistente', 'Accept-Profile': 'asistente' } };

interface AccionCfg {
  clave: string;
  nombre: string;
  descripcion: string;
  riesgo: RiesgoAsistente;
  activo: boolean;
  perfiles: { perfil_id: string; enabled: boolean }[];
}
interface DispositivoCfg {
  id: string;
  canal: 'whatsapp' | 'web' | 'app';
  identificador: string;
  activo: boolean;
  usuario_id: string;
  username: string | null;
}
interface ConfigData { acciones: AccionCfg[]; dispositivos: DispositivoCfg[] }
interface Perfil { id: string; nombre: string; nivel_acceso: number }
interface Usuario { id: string; username: string; email: string | null }

type VozModo = 'desactivado' | 'dictado' | 'estructurado' | 'completo';
const VOZ_MODOS: { v: VozModo; l: string; d: string; disponible: boolean }[] = [
  { v: 'desactivado', l: 'Desactivado', d: 'Sin orbe de voz', disponible: true },
  { v: 'dictado', l: 'Dictado', d: 'Escucha y transcribe tal cual al campo activo (STT local, el audio no sale del servidor)', disponible: true },
  { v: 'estructurado', l: 'Estructurado', d: 'Además, la IA propone los campos del formulario clínico desde el dictado (el médico acepta)', disponible: true },
  { v: 'completo', l: 'Completo', d: 'Además, comandos de voz y sugerencias CIE-10 (Fases 3-4)', disponible: false },
];

const CANALES: { v: DispositivoCfg['canal']; l: string; ph: string }[] = [
  { v: 'whatsapp', l: 'WhatsApp', ph: '+5939XXXXXXXX' },
  { v: 'web', l: 'Web', ph: 'identificador del navegador/sesión' },
  { v: 'app', l: 'App móvil', ph: 'id del dispositivo' },
];

export function AsistenteConfig() {
  const qc = useQueryClient();
  const [modalVinculo, setModalVinculo] = useState(false);
  const [fUsuario, setFUsuario] = useState('');
  const [fCanal, setFCanal] = useState<DispositivoCfg['canal']>('whatsapp');
  const [fIdent, setFIdent] = useState('');

  const { data, isFetching } = useQuery<ConfigData>({
    queryKey: ['asistente-config'],
    queryFn: async () => (await apiClient.post('/rpc/asistente_config_listar', {}, ASIST)).data,
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: perfiles = [] } = useQuery<Perfil[]>({
    queryKey: ['asistente-perfiles'],
    queryFn: () => auth.list<Perfil>('perfiles', '?select=id,nombre,nivel_acceso&activo=eq.true&order=nivel_acceso.desc,nombre'),
  });
  const { data: usuarios = [] } = useQuery<Usuario[]>({
    queryKey: ['asistente-usuarios'],
    queryFn: () => auth.list<Usuario>('usuarios', '?select=id,username,email&activo=eq.true&order=username'),
  });
  const { data: estado } = useQuery<{ checklist?: Record<string, boolean> }>({
    queryKey: ['sys-ayuda-estado'],
    queryFn: async () => (await apiClient.post('/rpc/sys_ayuda_estado', {})).data,
    staleTime: 0, refetchOnMount: 'always',
  });
  const workerOk = estado?.checklist?.worker_asistente ?? false;

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['asistente-config'] });
    qc.invalidateQueries({ queryKey: ['sys-ayuda-estado'] });
  };

  const setAccion = useMutation({
    mutationFn: async (v: { accion: string; perfil: string; enabled: boolean }) =>
      (await apiClient.post('/rpc/asistente_accion_perfil_set',
        { _accion: v.accion, _perfil: v.perfil, _enabled: v.enabled }, ASIST)).data,
    onSuccess: invalidar,
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const setDispositivo = useMutation({
    mutationFn: async (v: { usuario: string; canal: string; identificador: string; activo: boolean }) =>
      (await apiClient.post('/rpc/asistente_dispositivo_set',
        { _usuario: v.usuario, _canal: v.canal, _identificador: v.identificador, _activo: v.activo }, ASIST)).data,
    onSuccess: (_d, v) => {
      invalidar();
      setModalVinculo(false);
      setFIdent('');
      toast.success(v.activo ? 'Dispositivo vinculado' : 'Dispositivo desactivado');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { data: voz } = useQuery<{ modo: VozModo; dictado: boolean }>({
    queryKey: ['voz-estado'],
    queryFn: async () => (await apiClient.post('/rpc/asistente_voz_estado', {}, ASIST)).data,
    staleTime: 0, refetchOnMount: 'always',
  });
  const setVozModo = useMutation({
    mutationFn: async (modo: VozModo) =>
      (await apiClient.post('/rpc/asistente_voz_modo_set', { _modo: modo }, ASIST)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['voz-estado'] });
      toast.success('Modo de voz actualizado');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const habilitada = (a: AccionCfg, perfilId: string) =>
    a.perfiles.some((p) => p.perfil_id === perfilId && p.enabled);

  const dispositivos = data?.dispositivos ?? [];
  const getDispVal = useCallback((d: DispositivoCfg, key: string): string | number | null => {
    switch (key) {
      case 'canal': return d.canal;
      case 'identificador': return d.identificador;
      case 'usuario': return d.username;
      case 'activo': return d.activo ? 1 : 0;
      default: return null;
    }
  }, []);
  const sortDisp = useSort(dispositivos, getDispVal);

  const vincular = () => {
    if (!fUsuario) { toast.error('Selecciona el usuario dueño del dispositivo'); return; }
    if (!fIdent.trim()) { toast.error('Escribe el identificador del canal (p.ej. el nº de WhatsApp)'); return; }
    setDispositivo.mutate({ usuario: fUsuario, canal: fCanal, identificador: fIdent.trim(), activo: true });
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Bot className="w-5 h-5" /> Asistente — Configuración
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </h1>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${workerOk
          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
          : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
          {workerOk ? 'Worker conectado' : 'Worker desconectado'}
        </span>
      </div>
      {!workerOk && (
        <p className="text-sm text-muted-foreground">
          La configuración se guarda igual, pero el asistente no responderá hasta que TI arranque el
          worker (servicio <span className="font-mono">asistente/</span> con la API key de Anthropic en su .env).
        </p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Mic className="w-4 h-4" /> Asistente de voz (orbe) — modo de esta empresa</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            El orbe aparece a los perfiles con la acción <span className="font-medium">Dictado por voz</span> marcada
            en la matriz de abajo. Requiere los servicios <span className="font-mono">voice-gateway</span> y{' '}
            <span className="font-mono">stt-whisper</span> corriendo.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {VOZ_MODOS.map((m) => {
              const activo = (voz?.modo ?? 'desactivado') === m.v;
              return (
                <button
                  key={m.v}
                  type="button"
                  disabled={!m.disponible || setVozModo.isPending}
                  onClick={() => setVozModo.mutate(m.v)}
                  className={`text-left rounded-lg border p-3 transition-colors ${activo
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-border hover:bg-muted/50'} ${!m.disponible ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="text-sm font-medium flex items-center gap-2">
                    {m.l}
                    {!m.disponible && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">próximamente</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{m.d}</div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Acciones por perfil (esta empresa)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Marca qué puede pedir cada perfil. Las de riesgo <span className="font-medium">consulta</span> responden
            directo; las <span className="font-medium">operativas</span> piden un «sí» antes de ejecutar; las{' '}
            <span className="font-medium">críticas</span> exigen confirmación reforzada. Sin marca, la acción no existe
            para ese perfil. Los cambios aplican en la siguiente conversación.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <th className="py-2 pr-3 text-left">Acción</th>
                  {perfiles.map((p) => (
                    <th key={p.id} className="py-2 px-2 text-center whitespace-nowrap">
                      {p.nombre}
                      <div className="text-[10px] font-normal">nivel {p.nivel_acceso}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.acciones ?? []).map((a, i) => (
                  <tr key={a.clave} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0 ${RIESGO_BADGE[a.riesgo].cls}`}>
                          {RIESGO_BADGE[a.riesgo].label}
                        </span>
                        <span className="font-medium">{a.nombre}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">{a.descripcion}</div>
                    </td>
                    {perfiles.map((p) => (
                      <td key={p.id} className="py-2 px-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-input cursor-pointer"
                          checked={habilitada(a, p.id)}
                          disabled={setAccion.isPending}
                          onChange={(e) => setAccion.mutate({ accion: a.clave, perfil: p.id, enabled: e.target.checked })}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {data && !data.acciones?.length && (
                  <tr><td colSpan={2 + perfiles.length} className="py-4 text-center text-muted-foreground">
                    No hay acciones en el catálogo (aplica el script 311).
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Dispositivos vinculados (esta empresa)</CardTitle>
            <Button size="sm" onClick={() => setModalVinculo(true)}>
              <Plus className="w-4 h-4 mr-1" /> Vincular dispositivo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Cada nº de WhatsApp (o dispositivo) se asocia a UN usuario: es su identidad ante el asistente —
            trátalo como una credencial. Un canal no vinculado no recibe información del ERP.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <SortTh col="canal" sort={sortDisp}>Canal</SortTh>
                  <SortTh col="identificador" sort={sortDisp}>Identificador</SortTh>
                  <SortTh col="usuario" sort={sortDisp}>Usuario</SortTh>
                  <SortTh col="activo" sort={sortDisp} align="center">Estado</SortTh>
                  <th className="py-2 pr-3" />
                </tr>
              </thead>
              <tbody>
                {sortDisp.sorted.map((d, i) => (
                  <tr key={d.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3">{CANALES.find((c) => c.v === d.canal)?.l ?? d.canal}</td>
                    <td className="py-2 pr-3 font-mono">{d.identificador}</td>
                    <td className="py-2 pr-3">{d.username ?? '—'}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.activo
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'}`}>
                        {d.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <Button
                        size="sm" variant="outline"
                        disabled={setDispositivo.isPending}
                        onClick={() => setDispositivo.mutate({
                          usuario: d.usuario_id, canal: d.canal, identificador: d.identificador, activo: !d.activo,
                        })}
                      >
                        {d.activo ? 'Desactivar' : 'Activar'}
                      </Button>
                    </td>
                  </tr>
                ))}
                {data && !dispositivos.length && (
                  <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">
                    No hay dispositivos vinculados — el asistente no responderá a nadie de esta empresa.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {dispositivos.length > 0 && (
            <div className="text-xs text-muted-foreground border-t pt-2">
              {dispositivos.length} dispositivo(s) · {dispositivos.filter((d) => d.activo).length} activo(s)
            </div>
          )}
        </CardContent>
      </Card>

      {modalVinculo && (
        <Modal title="Vincular dispositivo" onClose={() => setModalVinculo(false)}>
          <div className="space-y-3">
            <Campo label="Usuario dueño del dispositivo">
              <select className={fieldCls} value={fUsuario} onChange={(e) => setFUsuario(e.target.value)}>
                <option value="">{SELECT_PLACEHOLDER}</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}{u.email ? ` (${u.email})` : ''}</option>
                ))}
              </select>
            </Campo>
            <Campo label="Canal">
              <select className={fieldCls} value={fCanal}
                      onChange={(e) => setFCanal(e.target.value as DispositivoCfg['canal'])}>
                {CANALES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Campo>
            <Campo label="Identificador">
              <input className={fieldCls} value={fIdent} onChange={(e) => setFIdent(e.target.value)}
                     placeholder={CANALES.find((c) => c.v === fCanal)?.ph} />
            </Campo>
            <p className="text-xs text-muted-foreground">
              Para WhatsApp usa el número en formato internacional (+5939…). Si el identificador ya estaba
              vinculado, se reasigna al usuario seleccionado.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalVinculo(false)}>Cancelar</Button>
              <Button onClick={vincular} isLoading={setDispositivo.isPending}>Vincular</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
